/**
 * Spinner - The Intelligent Standard
 *
 * A sophisticated loading indicator that matches the brand.
 * Uses a grid-based animation that echoes the spreadsheet motif.
 */

interface SpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  color?: string;
}

export function Spinner({ size = 'md', color }: SpinnerProps) {
  const sizes = {
    sm: { dot: 4, gap: 2 },
    md: { dot: 6, gap: 3 },
    lg: { dot: 8, gap: 4 },
  };

  const s = sizes[size];
  const dotColor = color || 'currentColor';

  return (
    <div
      className="arena-spinner"
      style={{ gap: s.gap }}
      role="status"
      aria-label="Loading"
    >
      {[0, 1, 2, 3].map(i => (
        <div
          key={i}
          className="arena-spinner-dot"
          style={{
            width: s.dot,
            height: s.dot,
            background: dotColor,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Full-page loading state with centered spinner
 */
export function PageLoader({ message }: { message?: string }) {
  return (
    <div
      className="min-h-[60vh] flex flex-col items-center justify-center animate-fade-in"
      style={{ color: '#6B6B6B' }}
    >
      <Spinner size="lg" />
      {message && <p className="mt-4 text-sm">{message}</p>}
    </div>
  );
}

/**
 * Inline loading state
 */
export function InlineLoader({ message }: { message?: string }) {
  return (
    <div className="flex items-center gap-3" style={{ color: '#6B6B6B' }}>
      <Spinner size="sm" />
      {message && <span className="text-sm">{message}</span>}
    </div>
  );
}

/**
 * Skeleton loader for content placeholders
 */
export function Skeleton({
  width,
  height,
  className = '',
}: {
  width?: string | number;
  height?: string | number;
  className?: string;
}) {
  return (
    <div
      className={`skeleton-arena ${className}`}
      style={{
        width: typeof width === 'number' ? `${width}px` : width,
        height: typeof height === 'number' ? `${height}px` : height,
      }}
    />
  );
}
